import { z } from "zod";
import {
  Base64,
  DeviceId,
  EnvKeyName,
  FileId,
  FileKind,
  FileMode,
  Hash256,
  Hlc,
  OpId,
  ProjectId,
  RuleId,
  SafeLocalPath,
  SafeRelPath,
} from "./primitives.js";

/** How a project is identified across devices. */
export const ProjectIdentity = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("git_remote"), url: z.string().min(1).max(2048) }),
  z.object({
    kind: z.literal("home_path"),
    path: z
      .string()
      .min(1)
      .max(4096)
      .refine((p) => p === "." || SafeRelPath.safeParse(p).success, "unsafe path"),
  }),
]);
export type ProjectIdentity = z.infer<typeof ProjectIdentity>;

export const Rule = z.object({
  /** Glob relative to `root`, or absolute with `~` prefix when root is omitted. */
  pattern: z.string().min(1).max(1024),
  mode: FileMode,
  /** Root directory the pattern is evaluated under; `~` expands to the home directory. */
  root: z.string().min(1).max(1024).default("~"),
  /** Globs excluded from the match. */
  exclude: z.array(z.string().min(1).max(1024)).default([]),
  /** When set, the rule only applies to this device (per-device override). */
  device_id: DeviceId.nullable().default(null),
  enabled: z.boolean().default(true),
});
export type Rule = z.infer<typeof Rule>;

/** One entry in an env file's layout. `raw` holds the exact original line(s). */
export const LayoutItem = z.discriminatedUnion("t", [
  z.object({ t: z.literal("key"), key: z.string(), raw: z.string() }),
  z.object({ t: z.literal("comment"), raw: z.string() }),
  z.object({ t: z.literal("blank"), raw: z.string() }),
  z.object({ t: z.literal("raw"), raw: z.string() }),
]);
export type LayoutItem = z.infer<typeof LayoutItem>;

export const Layout = z.object({
  items: z.array(LayoutItem),
  /** Whether the file ended with a trailing newline. */
  trailing_newline: z.boolean(),
});
export type Layout = z.infer<typeof Layout>;

export const BlobContent = z.discriminatedUnion("kind", [
  /** Small blob: plaintext bytes carried inline (the whole op is encrypted). */
  z.object({ kind: z.literal("inline"), data: Base64.max(((128 * 1024) / 3) * 4 + 8) }),
  /** Large blob: encrypted with a random content key and stored in R2 under `ref`. */
  z.object({ kind: z.literal("r2"), ref: z.string().min(1).max(256), key: Base64 }),
]);
export type BlobContent = z.infer<typeof BlobContent>;

export const InventoryKind = z.enum(["git_repos", "brewfile", "claude_plugins", "tool_versions"]);
export type InventoryKind = z.infer<typeof InventoryKind>;

/** How one checkout of a project on one device takes part in sync. */
export const CheckoutStatus = z.enum(["active", "pending", "ignored", "removed"]);
export type CheckoutStatus = z.infer<typeof CheckoutStatus>;

/** Which side wins when a held file is resolved. */
export const HoldResolution = z.enum(["use_vault", "use_local"]);
export type HoldResolution = z.infer<typeof HoldResolution>;

export const Op = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("ProjectUpsert"),
    project_id: ProjectId,
    identity: ProjectIdentity,
    name: z.string().min(1).max(256),
    hlc: Hlc,
  }),
  z.object({
    type: z.literal("DevicePath"),
    project_id: ProjectId,
    device_id: DeviceId,
    local_path: SafeLocalPath,
    hlc: Hlc,
  }),
  z.object({ type: z.literal("RuleUpsert"), rule_id: RuleId, rule: Rule, hlc: Hlc }),
  z.object({ type: z.literal("RuleDelete"), rule_id: RuleId, hlc: Hlc }),
  z.object({
    type: z.literal("FileTrack"),
    file_id: FileId,
    project_id: ProjectId,
    rel_path: SafeRelPath,
    kind: FileKind,
    mode: FileMode,
    hlc: Hlc,
  }),
  z.object({ type: z.literal("FileUntrack"), file_id: FileId, hlc: Hlc }),
  z.object({
    type: z.literal("EnvKeySet"),
    file_id: FileId,
    key: EnvKeyName,
    value: z.string().max(65536),
    /** HLCs of the versions this write supersedes (the heads the writer saw). */
    prev: z.array(Hlc).default([]),
    hlc: Hlc,
  }),
  z.object({
    type: z.literal("EnvKeyDelete"),
    file_id: FileId,
    key: EnvKeyName,
    prev: z.array(Hlc).default([]),
    hlc: Hlc,
  }),
  z.object({ type: z.literal("EnvLayoutSet"), file_id: FileId, layout: Layout, hlc: Hlc }),
  z.object({
    type: z.literal("BlobVersion"),
    file_id: FileId,
    content_hash: Hash256,
    size: z.int().nonnegative(),
    content: BlobContent,
    /** Content hashes of the versions this one derives from (two for a merge result). */
    parents: z.array(Hash256).default([]),
    /** Unix permission bits (e.g. 0o600 = 384). */
    mode_bits: z.int().min(0).max(0o7777).nullable().default(null),
    hlc: Hlc,
  }),
  z.object({
    type: z.literal("Inventory"),
    device_id: DeviceId,
    kind: InventoryKind,
    payload: z.unknown(),
    hlc: Hlc,
  }),
  /** A checkout of a project on a device and whether it syncs; `DevicePath` is the first active one. */
  z.object({
    type: z.literal("CheckoutUpsert"),
    project_id: ProjectId,
    device_id: DeviceId,
    local_path: SafeLocalPath,
    status: CheckoutStatus,
    hlc: Hlc,
  }),
  /** Content a device found at a never-synced path that differs from the vault; held until resolved. */
  z.object({
    type: z.literal("FileHold"),
    file_id: FileId,
    device_id: DeviceId,
    local_path: SafeLocalPath,
    content_hash: Hash256,
    size: z.int().nonnegative(),
    content: BlobContent,
    hlc: Hlc,
  }),
  z.object({
    type: z.literal("FileHoldResolve"),
    file_id: FileId,
    device_id: DeviceId,
    local_path: SafeLocalPath,
    content_hash: Hash256,
    resolution: HoldResolution,
    hlc: Hlc,
  }),
]);
export type Op = z.infer<typeof Op>;
export type OpType = Op["type"];

/** What the server stores and relays: an opaque ciphertext plus routing metadata. */
export const EncryptedOp = z.object({
  v: z.literal(1),
  op_id: OpId,
  device_id: DeviceId,
  /** base64(nonce || ciphertext). AAD binds vault_id, op_id and device_id. */
  ct: Base64,
});
export type EncryptedOp = z.infer<typeof EncryptedOp>;

export const StoredOp = EncryptedOp.extend({
  seq: z.int().positive(),
  created_at: z.int().nonnegative(),
});
export type StoredOp = z.infer<typeof StoredOp>;

/** Inline ops are capped; larger file contents go to R2. */
export const MAX_OP_BYTES = 256 * 1024;
export const MAX_INLINE_BLOB_BYTES = 128 * 1024;
export const MAX_PUSH_BATCH = 200;
/** Total base64 ciphertext per push, well under the 32 MiB Durable Object RPC limit. */
export const MAX_PUSH_BYTES = 8 * 1024 * 1024;
export const MAX_PULL_BATCH = 500;
